import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectedPrice, Unit } from '@product/const';
import { CounterSize } from '@shared/models/counter.model';
import { BehaviorSubject } from 'rxjs';

export enum ItemCounterThemes {
  default = 'default',
  concrete = 'concrete',
}

@Component({
  selector: 'sd-item-counter',
  templateUrl: './item-counter.component.html',
  styleUrls: ['./item-counter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SdvorItemCounterComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SdvorItemCounterComponent implements AfterViewInit, ControlValueAccessor {
  @Input() inCart = false;
  @Input() useDecimals = false;
  @Input() loading = false;
  @Input() min = 1;
  @Input() step = 1;
  @Input() allowZero = false;
  @Input() zeroFocusEmitValue = true; // если false, 0 и курсор на инпуте - не будет эмита изменения значения
  @Input() size: CounterSize = CounterSize.S;
  @Input() selectedPrice: SelectedPrice;
  @Input() theme: ItemCounterThemes = ItemCounterThemes.default;
  @ViewChild('quantityInput') quantityInput: ElementRef<HTMLInputElement>;

  saleQuantity$ = new BehaviorSubject<number>(0);

  // Выбранное значение превышено
  valueExceeded$ = new BehaviorSubject(false);

  // показывать внутри счетчика перевод в продажные ед измерения
  @Input() set showSaleUnit(value: boolean) {
    this._showSaleUnit = Boolean(value);
  }

  get showSaleUnit(): boolean {
    return this._showSaleUnit && !this.selectedUnitIsSaleUnit;
  }

  get selectedUnitIsSaleUnit(): boolean {
    return this.selectedPrice.unit?.code === this.saleUnit.code;
  }

  @Input() set saleQuantity(quantity: number) {
    this.saleQuantity$.next(quantity);
  }

  get saleQuantity(): number {
    return this.saleQuantity$.getValue();
  }

  @Input()
  set unitName(value: string) {
    this._unitName = value;
  }

  get unitName(): string {
    return this._unitName;
  }

  @Input() set max(value: number) {
    this._max = value;
    this.valueExceeded$.next(false);
  }

  get max(): number {
    return this._max;
  }

  get saleUnit(): Unit {
    return this.selectedPrice.saleUnit;
  }

  quantityControl = new FormControl<number | null>(null);

  set value(val: string | number) {
    this.quantityControl.setValue(+val, { emitEvent: false });
  }

  get value(): number {
    return this.quantityControl.value ?? 0;
  }

  disabled = false;

  @HostBinding('class.readonly') @Input() readonly = false;

  @HostBinding('class')
  get classNames(): string[] {
    return ['sd-counter', this.theme && `sd-counter-theme-${this.theme}`].filter(Boolean);
  }

  private _isInputFocused = false;
  private _unitName: string;
  private _max = 1000000;
  private _showSaleUnit = false;

  /**
   * Cинхронизация контрола с состоянием приложения реализована через таймаут.
   * Если достаточно часто изменять количество товара, но чуть медленнее чем дебаунс,
   * так чтобы запросы шли друг за другом, происходит затирание последнего введенного
   * значения состоянием из стора.
   *
   * Чтобы решить эту проблему реализована логика:
   * После того как пользователь изменил значение в счетчике, мы ожидаем <syncTimeout> мс.,
   * и только после этого проверяем совпадает ли значение счетчика со значением в сторе,
   * если не совпадает, то синхронизируем значение счетчика.
   */
  private syncTimeout = 5000;
  private syncTimeoutId?: NodeJS.Timeout;
  private syncValue?: number;

  constructor(
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  ngAfterViewInit(): void {
    this.quantityControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value !== null) {
        this.setValue(value);
      } else {
        this.setValue(0);
      }
    });
  }

  writeValue(value: number): void {
    if (this._isInputFocused) {
      return;
    }

    if (this.syncTimeoutId) {
      this.syncValue = value;

      return;
    }

    this.setValue(value, false);
  }

  setSyncTimeout(): void {
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }

    this.syncTimeoutId = setTimeout(() => {
      this.syncTimeoutId = undefined;

      if (this.syncValue && this.value !== this.syncValue) {
        this.setValue(this.syncValue, false);
        this.syncValue = undefined;
      }
    }, this.syncTimeout);
  }

  registerOnChange(fn: (_: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  increment(): void {
    if (this.useDecimals) {
      this.setValue(this.value + this.step);

      return;
    }

    this.setValue(Math.floor(this.value) + this.step);
  }

  decrement(): void {
    if (this.useDecimals) {
      this.setValue(this.value - this.step);

      return;
    }

    this.setValue(Math.floor(this.value) - this.step);
  }

  onEnterPress(): void {
    this.quantityInput.nativeElement.blur();
  }

  onFocus(): void {
    this._isInputFocused = true;
  }

  onBlur(): void {
    this._isInputFocused = false;
    this.setValue(this.value, true);
  }

  private guardZeroFocused(value: number): boolean {
    if (!this.zeroFocusEmitValue) {
      if (value === 0 && this._isInputFocused) {
        return true;
      }
    }

    return false;
  }

  private setValue(value: number, callChangeEvent: boolean = true): void {
    if (this.guardZeroFocused(value)) {
      return;
    }

    this.value = this.getValidCount(value);

    this.valueExceeded$.next(value > this.max);

    if (callChangeEvent) {
      this.setSyncTimeout();
      this.onChange(this.value);
    }

    this.onTouched();
  }

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  private onChange(_: number): void {}

  private onTouched(): void {}

  private getValidCount(value: number): number {
    if (value < this.min && !(value === 0 && this.allowZero)) {
      value = this.min;
    }
    if (this.max && value > this.max) {
      value = this.max;
    }

    // кратность округляем в большую сторону, ЕСЛИ это продажная единица
    if (value % this.step !== 0 && this.selectedUnitIsSaleUnit) {
      value = Math.ceil(value / this.step) * this.step;
    }

    return value;
  }
}
